/**
 * Add Configs Dialog Component
 * Two-step dialog: 1) Select configs, 2) Set template values
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimarySmallButton, BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { Template, ConfigRegistryItem } from '../types/configRegistryTypes';
import { getConfigGlobalValue, GlobalConfigValues } from '../utils/templateValueHelpers';
import { ColumnHeader } from './ColumnHeader';
import { SearchableSelect } from 'components/common/SearchableSelect';

interface AddConfigsDialogProps {
  open: boolean;
  onClose: () => void;
  template: Template;
  /** Full member config ids — required when template.items only holds the current page */
  memberConfigIds?: string[];
  onConfigsAdded: () => void;
}

const STEPS = ['Select Configs', 'Set Values'];

export const AddConfigsDialog = ({
  open,
  onClose,
  template,
  memberConfigIds,
  onConfigsAdded,
}: AddConfigsDialogProps) => {
  const { rc } = useGlobalConfig();

  // Lookup option type for filters
  interface LookupOption {
    documentId: string;
    displayName: string;
    code: string;
  }

  const [configs, setConfigs] = useState<ConfigRegistryItem[]>([]);
  const [configTypes, setConfigTypes] = useState<string[]>([]);
  const [components, setComponents] = useState<LookupOption[]>([]);
  const [classifications, setClassifications] = useState<LookupOption[]>([]);
  // Default values live on the GLOBAL template, not on the config record. The
  // list API ships them per row; this map is the fallback for backends that
  // don't, and stays empty otherwise.
  const [globalValues, setGlobalValues] = useState<GlobalConfigValues>(new Map());
  const [globalValuesLoading, setGlobalValuesLoading] = useState(false);
  const [globalValuesFailed, setGlobalValuesFailed] = useState(false);
  /** Guards the one-shot decision: does the list already carry GLOBAL values? */
  const globalFallbackRef = useRef<'pending' | 'resolved'>('pending');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalConfigs, setTotalConfigs] = useState(0);

  // Step management
  const [activeStep, setActiveStep] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConfigType, setSelectedConfigType] = useState<string>('');
  const [selectedComponent, setSelectedComponent] = useState<string>('');
  const [selectedClassification, setSelectedClassification] = useState<string>('');

  const [selectedConfigs, setSelectedConfigs] = useState<Set<string>>(new Set());
  // Store full config objects for selected configs to persist across pagination/filter changes
  const [selectedConfigsMap, setSelectedConfigsMap] = useState<Map<string, ConfigRegistryItem>>(new Map());
  // Track custom template values for each config (key: documentId, value: template value)
  const [customValues, setCustomValues] = useState<Record<string, string>>({});

  // Get existing config IDs in template (server-provided list preferred —
  // template.items may only hold the currently displayed page)
  const existingConfigIds = useMemo(() => {
    if (memberConfigIds?.length) return new Set(memberConfigIds);
    return new Set(
      template.items
        .map(item => item.configRegistryConfig?.documentId)
        .filter(Boolean) as string[]
    );
  }, [memberConfigIds, template.items]);

  // Fetch metadata (config types, components, classifications) on mount
  useEffect(() => {
    if (!open) return;

    const fetchMetadata = async () => {
      try {
        const [typesRes, schemaRes] = await Promise.all([
          ConfigRegistryApis.getConfigTypes(rc),
          ConfigRegistryApis.getUISchema(rc),
        ]);

        if (typesRes.data) {
          const types = (typesRes.data as any).data || typesRes.data;
          setConfigTypes(Array.isArray(types) ? types : []);
        }

        if (schemaRes.data) {
          const schemaData = schemaRes.data as any;
          const lookupTypes = schemaData.lookupTypes || schemaData.lookupFields || [];

          // Extract components (UPPERCASE code) - store full objects with documentId
          const componentLookup = lookupTypes.find((lt: any) => lt.code === 'COMPONENT');
          if (componentLookup) {
            const componentOptions = (componentLookup.lookups || componentLookup.options || [])
              .map((opt: any) => ({
                documentId: opt.documentId || opt.value,
                displayName: opt.displayName || opt.label || opt.code,
                code: opt.code,
              }))
              .filter((opt: LookupOption) => opt.documentId);
            setComponents(componentOptions);
          }

          // Extract categorizations (UPPERCASE code) - store full objects with documentId
          const categorizationLookup = lookupTypes.find((lt: any) => lt.code === 'CATEGORIZATION');
          if (categorizationLookup) {
            const classificationOptions = (categorizationLookup.lookups || categorizationLookup.options || [])
              .map((opt: any) => ({
                documentId: opt.documentId || opt.value,
                displayName: opt.displayName || opt.label || opt.code,
                code: opt.code,
              }))
              .filter((opt: LookupOption) => opt.documentId);
            setClassifications(classificationOptions);
          }

        }
      } catch (err: any) {
        console.error('Failed to fetch metadata:', err);
      }
    };

    fetchMetadata();
  }, [open, rc]);

  useEffect(() => {
    if (!open) return;
    globalFallbackRef.current = 'pending';
    setGlobalValues(new Map());
    setGlobalValuesFailed(false);
    setGlobalValuesLoading(true);
  }, [open]);

  // The list API ships each row's GLOBAL value in `templateItems`. Only fall
  // back to fetching the whole GLOBAL template when it doesn't — that keeps a
  // ~2700-row request off the hot path on any backend that enriches the list.
  useEffect(() => {
    if (!open || isLoading || configs.length === 0) return;
    if (globalFallbackRef.current !== 'pending') return;
    globalFallbackRef.current = 'resolved';

    if (configs.some(config => config.templateItems !== undefined)) {
      setGlobalValuesLoading(false);
      return;
    }

    let cancelled = false;
    ConfigRegistryApis.getGlobalConfigValues(rc).then(({ values, error }) => {
      if (cancelled) return;
      setGlobalValues(values);
      if (error) setGlobalValuesFailed(true);
      setGlobalValuesLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, rc, isLoading, configs]);

  /** GLOBAL default for a config — '' when it has none */
  const resolveDefaultValue = useCallback(
    (config: ConfigRegistryItem) => getConfigGlobalValue(config, globalValues),
    [globalValues]
  );

  /** What the Default Value column shows: '…' while the GLOBAL fetch is in flight */
  const displayDefaultValue = useCallback(
    (config: ConfigRegistryItem) =>
      resolveDefaultValue(config) || (globalValuesLoading ? '…' : '-'),
    [resolveDefaultValue, globalValuesLoading]
  );

  // Fetch configs with filters and pagination
  useEffect(() => {
    if (!open) return;

    const fetchConfigs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await ConfigRegistryApis.getConfigs(rc, {
          page,
          pageSize,
          configType: selectedConfigType || undefined,
          component: selectedComponent || undefined,
          classification: selectedClassification || undefined,
          search: searchQuery || undefined,
          // Server-side exclusion — keeps the counts and page windows about what
          // is actually addable. Rows are still checked against
          // existingConfigIds below, since an older backend ignores this param.
          excludeTemplate: template.documentId,
        });

        if (response.data) {
          setConfigs(response.data);
        }

        // Handle pagination metadata
        const meta = response.meta as {
          page?: number;
          pageSize?: number;
          pageCount?: number;
          total?: number;
        } | undefined;

        if (meta) {
          setTotalConfigs(meta.total || 0);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch configs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, [open, rc, template.documentId, page, pageSize, selectedConfigType, selectedComponent, selectedClassification, searchQuery]);

  // Configs already in the template stay VISIBLE but disabled. Dropping them
  // from the rows made a page whose every row is a member render blank while the
  // header still counted it as a full page — pagination is server-side, so the
  // page can't be back-filled client-side.
  const selectableConfigs = useMemo(
    () => configs.filter(config => !existingConfigIds.has(config.documentId)),
    [configs, existingConfigIds]
  );
  const memberCountOnPage = configs.length - selectableConfigs.length;

  // Calculate pagination
  const totalPages = Math.ceil(totalConfigs / pageSize);
  const currentPageStart = (page - 1) * pageSize + 1;
  const currentPageEnd = Math.min(page * pageSize, totalConfigs);

  // A filter that shrinks the result set can leave `page` past the last page —
  // clamp so the grid never sits on an out-of-range blank page.
  useEffect(() => {
    if (isLoading) return;
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [isLoading, page, totalPages]);

  const handleToggleConfig = (documentId: string) => {
    if (existingConfigIds.has(documentId)) return;
    const newSelected = new Set(selectedConfigs);
    const newSelectedMap = new Map(selectedConfigsMap);
    if (newSelected.has(documentId)) {
      newSelected.delete(documentId);
      newSelectedMap.delete(documentId);
    } else {
      newSelected.add(documentId);
      // Store full config object for persistence across pagination/filter changes
      const config = configs.find(c => c.documentId === documentId);
      if (config) newSelectedMap.set(documentId, config);
    }
    setSelectedConfigs(newSelected);
    setSelectedConfigsMap(newSelectedMap);
  };

  const handleValueChange = (documentId: string, value: string) => {
    setCustomValues(prev => ({
      ...prev,
      [documentId]: value,
    }));
  };

  // Current page selection state — over selectable rows only, so a page of
  // already-added configs doesn't report itself as "all selected"
  const selectedOnCurrentPage = selectableConfigs.filter(c => selectedConfigs.has(c.documentId)).length;
  const allCurrentPageSelected = selectableConfigs.length > 0 && selectedOnCurrentPage === selectableConfigs.length;
  const someCurrentPageSelected = selectedOnCurrentPage > 0 && selectedOnCurrentPage < selectableConfigs.length;

  const handleSelectAll = () => {
    const newSelected = new Set(selectedConfigs);
    const newSelectedMap = new Map(selectedConfigsMap);

    if (allCurrentPageSelected) {
      // Deselect all configs on current page only
      selectableConfigs.forEach(config => {
        newSelected.delete(config.documentId);
        newSelectedMap.delete(config.documentId);
      });
    } else {
      // Select all configs on current page only
      selectableConfigs.forEach(config => {
        newSelected.add(config.documentId);
        // Store full config object for persistence
        newSelectedMap.set(config.documentId, config);
      });
    }

    setSelectedConfigs(newSelected);
    setSelectedConfigsMap(newSelectedMap);
  };

  // Filter change handlers - reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleConfigTypeChange = (value: string) => {
    setSelectedConfigType(value);
    setPage(1);
  };

  const handleComponentChange = (value: string) => {
    setSelectedComponent(value);
    setPage(1);
  };

  const handleClassificationChange = (value: string) => {
    setSelectedClassification(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedConfigType('');
    setSelectedComponent('');
    setSelectedClassification('');
    setPage(1);
  };

  // Get selected configs as array for step 2 - use stored map to persist across pages/filters
  const selectedConfigsList = useMemo(() => {
    return Array.from(selectedConfigsMap.values());
  }, [selectedConfigsMap]);

  // Step 2 pre-fills each field from the GLOBAL default at render time rather
  // than seeding customValues here — seeding captured '' for any config selected
  // before the GLOBAL values landed, and that stale blank was then saved.
  const handleNext = () => {
    if (activeStep === 0 && selectedConfigs.size > 0) setActiveStep(1);
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleAddConfigs = async () => {
    if (selectedConfigs.size === 0) return;

    setIsSaving(true);
    try {
      // Upsert ONLY the newly selected configs — existing template values are untouched.
      // Use selectedConfigsMap to get config data (persists across pagination/filter changes)
      const newValuesPayload = Array.from(selectedConfigs).map(docId => {
        const config = selectedConfigsMap.get(docId);
        // Use the edited value if the user touched the field, else the GLOBAL default
        const templateValue = customValues[docId] !== undefined
          ? customValues[docId]
          : (config ? resolveDefaultValue(config) || null : null);
        return { configDocumentId: docId, value: templateValue };
      });

      await ConfigRegistryApis.upsertTemplateValues(rc, template.documentId, newValuesPayload);

      onConfigsAdded();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add configs');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedConfigs(new Set());
    setSelectedConfigsMap(new Map());
    setCustomValues({});
    setSearchQuery('');
    setSelectedConfigType('');
    setSelectedComponent('');
    setSelectedClassification('');
    // Reset paging too — reopening on a stale page looked like an empty result
    setPage(1);
    setPageSize(25);
    setActiveStep(0);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: '90vw',
          height: '90vh',
        }
      }}
    >
      <DialogTitle>
        Add Configs to Template
        <Typography variant="body2" color="text.secondary">
          {activeStep === 0 ? `Select configs to add to "${template.name}"` : `Set template values for selected configs`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {globalValuesFailed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Couldn&apos;t load GLOBAL template values — Default Value shows &quot;-&quot; for every
            config and new rows will be added empty. Reopen the dialog to retry.
          </Alert>
        )}

        {/* Step 1: Select Configs */}
        {activeStep === 0 && (
          <>
            {/* Search */}
            <Box sx={{ mb: 2 }}>
              <TextField
                placeholder="Search by config key or name..."
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <SearchableSelect
                size="small"
                sx={{ minWidth: 150, flex: 1 }}
                label="Config Type"
                value={selectedConfigType}
                onChange={handleConfigTypeChange}
                options={[{ value: '', label: 'All Types' }, ...configTypes.map(type => ({ value: type, label: type }))]}
              />

              <SearchableSelect
                size="small"
                sx={{ minWidth: 150, flex: 1 }}
                label="Component"
                value={selectedComponent}
                onChange={handleComponentChange}
                options={[{ value: '', label: 'All Components' }, ...components.map(comp => ({ value: comp.documentId, label: comp.displayName }))]}
              />

              <SearchableSelect
                size="small"
                sx={{ minWidth: 150, flex: 1 }}
                label="Classification"
                value={selectedClassification}
                onChange={handleClassificationChange}
                options={[{ value: '', label: 'All Classifications' }, ...classifications.map(cls => ({ value: cls.documentId, label: cls.displayName }))]}
              />

              <BglSecondarySmallButton
                onClick={handleClearFilters}
                sx={{ minWidth: 120 }}
              >
                Clear Filters
              </BglSecondarySmallButton>
            </Box>

            {/* Stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {totalConfigs === 0
                  ? 'No configs match these filters'
                  : `Page ${page} of ${totalPages || 1} • ${currentPageStart}-${currentPageEnd} of ${totalConfigs} configs`}
                {memberCountOnPage > 0
                  ? ` • ${memberCountOnPage} on this page already in "${template.name}"`
                  : ''}
              </Typography>
              <Chip
                label={`${selectedConfigs.size} selected`}
                color={selectedConfigs.size > 0 ? 'primary' : 'default'}
                size="small"
              />
            </Box>

            {/* Configs Table */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={someCurrentPageSelected}
                          checked={allCurrentPageSelected}
                          onChange={handleSelectAll}
                          disabled={selectableConfigs.length === 0}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Config Key" tooltipKey="configKey" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Display Name" tooltipKey="displayName" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Config Type" tooltipKey="configType" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Default Value" tooltipKey="defaultValue" />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {configs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No configs match these filters
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      configs.map(config => {
                        const isMember = existingConfigIds.has(config.documentId);
                        const isSelected = selectedConfigs.has(config.documentId);
                        const isJSON = config.configDataType === 'JSON';
                        const defaultValueStr = displayDefaultValue(config);

                        return (
                          <TableRow
                            key={config.documentId}
                            hover={!isMember}
                            selected={isSelected}
                            onClick={isMember ? undefined : () => handleToggleConfig(config.documentId)}
                            sx={{ cursor: isMember ? 'default' : 'pointer', opacity: isMember ? 0.55 : 1 }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={isSelected} disabled={isMember} />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  {config.configKey}
                                </Typography>
                                {isMember && (
                                  <Chip
                                    label="In template"
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {config.displayName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={config.configType} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  maxWidth: 300,
                                  maxHeight: 60,
                                  overflow: 'auto',
                                  p: 0.5,
                                  bgcolor: 'grey.50',
                                  borderRadius: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontFamily: isJSON ? 'monospace' : 'inherit',
                                    fontSize: '0.8rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {defaultValueStr}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination Controls */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <BglSecondarySmallButton
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isLoading}
                >
                  Previous
                </BglSecondarySmallButton>
                <Typography variant="body2" color="text.secondary">
                  Page {page} of {totalPages || 1}
                </Typography>
                <BglSecondarySmallButton
                  onClick={() => setPage(p => Math.min(Math.max(totalPages, 1), p + 1))}
                  disabled={page >= totalPages || isLoading || totalPages === 0}
                >
                  Next
                </BglSecondarySmallButton>
              </Box>
              <SearchableSelect
                size="small"
                sx={{ minWidth: 100 }}
                label="Per Page"
                value={String(pageSize)}
                onChange={(v) => {
                  if (!v) return;
                  setPageSize(Number(v));
                  setPage(1);
                }}
                options={[{ value: '25', label: '25' }, { value: '50', label: '50' }, { value: '100', label: '100' }]}
              />
            </Box>
          </>
        )}

        {/* Step 2: Set Values */}
        {activeStep === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set template values for the {selectedConfigsList.length} selected config(s).
              Each field is pre-filled with the config&apos;s GLOBAL default — edit the ones
              that should differ for this template.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                      <ColumnHeader label="Config Key" tooltipKey="configKey" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                      <ColumnHeader label="Display Name" tooltipKey="displayName" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '10%' }}>
                      <ColumnHeader label="Config Type" tooltipKey="configType" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '25%' }}>
                      <ColumnHeader label="Default Value" tooltipKey="defaultValue" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '50%' }}>
                      <ColumnHeader label="Template Value" tooltipKey="templateValue" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedConfigsList.map(config => {
                    const isJSON = config.configDataType === 'JSON';
                    const globalDefault = resolveDefaultValue(config);
                    const defaultValueStr = displayDefaultValue(config);

                    return (
                      <TableRow key={config.documentId} sx={{ '& > *': { verticalAlign: 'top !important' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {config.configKey}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {config.displayName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={config.configType} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              maxHeight: 120,
                              maxWidth: 300,
                              overflow: 'auto',
                              p: 1,
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: isJSON ? 'monospace' : 'inherit',
                                fontSize: '0.8rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.disabled',
                                fontStyle: 'italic',
                              }}
                            >
                              {defaultValueStr}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            placeholder={globalDefault || 'Enter value...'}
                            value={customValues[config.documentId] ?? globalDefault}
                            onChange={(e) => handleValueChange(config.documentId, e.target.value)}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: 'background.paper',
                              },
                              '& .MuiInputBase-input': {
                                fontFamily: isJSON ? 'monospace' : 'inherit',
                                fontSize: '0.85rem',
                                color: 'text.primary',
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <BglSecondarySmallButton onClick={handleClose} disabled={isSaving}>
          Cancel
        </BglSecondarySmallButton>

        {activeStep === 0 ? (
          <BglPrimarySmallButton
            onClick={handleNext}
            disabled={selectedConfigs.size === 0}
            endIcon={<ArrowForwardIcon />}
          >
            Next: Set Values
          </BglPrimarySmallButton>
        ) : (
          <>
            <BglSecondarySmallButton onClick={handleBack} disabled={isSaving} startIcon={<ArrowBackIcon />}>
              Back
            </BglSecondarySmallButton>
            <BglPrimarySmallButton
              onClick={handleAddConfigs}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Add {selectedConfigs.size} Config{selectedConfigs.size > 1 ? 's' : ''}
            </BglPrimarySmallButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddConfigsDialog;

